/* eslint-disable react/no-unescaped-entities */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Heart, Eye, Trash2, RefreshCw, Calendar, Users, MapPin, Phone, Mail } from 'lucide-react';
import { useAdmin } from '@/contexts/AdminContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WeddingEnquiries = () => {
  const { token } = useAdmin();
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);

  const fetchEnquiries = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/wedding-enquiries`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEnquiries(response.data);
    } catch (error) {
      toast.error('Failed to fetch wedding enquiries');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchEnquiries();
  }, [fetchEnquiries]);

  const handleStatusChange = async (enquiryId, newStatus) => {
    try {
      await axios.put(
        `${API}/admin/wedding-enquiries/${enquiryId}/status?status=${newStatus}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Status updated successfully');
      fetchEnquiries();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async (enquiryId) => {
    if (!window.confirm('Are you sure you want to delete this wedding enquiry?')) {
      return;
    }
    try {
      await axios.delete(`${API}/admin/wedding-enquiries/${enquiryId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Wedding enquiry deleted');
      fetchEnquiries();
    } catch (error) {
      toast.error('Failed to delete enquiry');
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      new: 'bg-blue-100 text-blue-800',
      contacted: 'bg-yellow-100 text-yellow-800',
      in_progress: 'bg-purple-100 text-purple-800',
      converted: 'bg-green-100 text-green-800',
      closed: 'bg-gray-100 text-gray-800'
    };
    return (
      <Badge className={variants[status] || variants.new}>
        {status?.replace('_', ' ').toUpperCase() || 'NEW'}
      </Badge>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDestination = (dest) => {
    if (!dest) return '-';
    const destNames = {
      udaipur: 'Udaipur, Rajasthan',
      goa: 'Goa',
      jaipur: 'Jaipur, Rajasthan',
      kerala: 'Kerala',
      thailand: 'Thailand',
      bali: 'Bali, Indonesia',
      dubai: 'Dubai, UAE',
      europe: 'Europe',
      other: 'Other / Custom'
    };
    return destNames[dest] || dest;
  };

  return (
    <div className="space-y-6" data-testid="wedding-enquiries-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Heart className="h-6 w-6 text-rose-500" />
            Wedding Enquiries
          </h1>
          <p className="text-slate-600 mt-1">Manage consultation requests from Weddingz by DNO</p>
        </div>
        <Button onClick={fetchEnquiries} variant="outline" data-testid="refresh-btn">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                <Heart className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{enquiries.length}</p>
                <p className="text-sm text-slate-500">Total Enquiries</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center">
                <Calendar className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{enquiries.filter(e => e.status === 'new').length}</p>
                <p className="text-sm text-slate-500">New / Pending</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                <Users className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{enquiries.filter(e => e.status === 'in_progress').length}</p>
                <p className="text-sm text-slate-500">In Progress</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                <Heart className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{enquiries.filter(e => e.status === 'converted').length}</p>
                <p className="text-sm text-slate-500">Converted</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Enquiries Table */}
      <Card>
        <CardHeader>
          <CardTitle>All Wedding Enquiries</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-slate-500">Loading...</div>
          ) : enquiries.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              No wedding enquiries yet. Enquiries from the Weddingz page will appear here.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Couple</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Destination</TableHead>
                  <TableHead>Wedding Date</TableHead>
                  <TableHead>Guests</TableHead>
                  <TableHead>Budget</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {enquiries.map((enquiry) => (
                  <TableRow key={enquiry.id} data-testid={`wedding-enquiry-row-${enquiry.id}`}>
                    <TableCell>
                      <div>
                        <div className="font-medium">{enquiry.name}</div>
                        {enquiry.partner_name && (
                          <div className="text-sm text-slate-500">& {enquiry.partner_name}</div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <div className="flex items-center gap-1">
                          <Phone className="h-3 w-3" /> {enquiry.phone}
                        </div>
                        <div className="flex items-center gap-1 text-slate-500">
                          <Mail className="h-3 w-3" /> {enquiry.email}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>{formatDestination(enquiry.destination)}</TableCell>
                    <TableCell>{enquiry.wedding_date || '-'}</TableCell>
                    <TableCell>{enquiry.guest_count || '-'}</TableCell>
                    <TableCell>{enquiry.budget || '-'}</TableCell>
                    <TableCell>
                      <Select
                        value={enquiry.status || 'new'}
                        onValueChange={(value) => handleStatusChange(enquiry.id, value)}
                      >
                        <SelectTrigger className="w-32" data-testid={`status-select-${enquiry.id}`}>
                          <SelectValue>{getStatusBadge(enquiry.status)}</SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="new">New</SelectItem>
                          <SelectItem value="contacted">Contacted</SelectItem>
                          <SelectItem value="in_progress">In Progress</SelectItem>
                          <SelectItem value="converted">Converted</SelectItem>
                          <SelectItem value="closed">Closed</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-sm text-slate-500">
                      {formatDate(enquiry.created_at)}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setSelectedEnquiry(enquiry);
                            setViewDialogOpen(true);
                          }}
                          data-testid={`view-enquiry-${enquiry.id}`}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-500 hover:text-red-700"
                          onClick={() => handleDelete(enquiry.id)}
                          data-testid={`delete-enquiry-${enquiry.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* View Enquiry Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Heart className="h-5 w-5 text-rose-500" />
              Wedding Enquiry Details
            </DialogTitle>
          </DialogHeader>
          {selectedEnquiry && (
            <div className="space-y-6">
              {/* Couple Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-500">Name</label>
                  <p className="text-lg">{selectedEnquiry.name}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-500">Partner's Name</label>
                  <p className="text-lg">{selectedEnquiry.partner_name || '-'}</p>
                </div>
              </div>

              {/* Contact Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-500">Email</label>
                  <p>{selectedEnquiry.email}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-500">Phone</label>
                  <p>{selectedEnquiry.phone}</p>
                </div>
              </div>

              {/* Wedding Details */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-500">Preferred Destination</label>
                  <p>{formatDestination(selectedEnquiry.destination)}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-500">Wedding Date/Month</label>
                  <p>{selectedEnquiry.wedding_date || '-'}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-500">Expected Guests</label>
                  <p>{selectedEnquiry.guest_count || '-'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-500">Budget Range</label>
                  <p>{selectedEnquiry.budget || '-'}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-500">Wedding Style</label>
                  <p>{selectedEnquiry.wedding_style || '-'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-500">Events Planned</label>
                  <p>{selectedEnquiry.events_planned || '-'}</p>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-500">Accommodation Needed</label>
                <p>{selectedEnquiry.accommodation_needed || '-'}</p>
              </div>

              {selectedEnquiry.special_requirements && (
                <div>
                  <label className="text-sm font-medium text-slate-500">Special Requirements</label>
                  <p className="bg-slate-50 p-3 rounded-lg">{selectedEnquiry.special_requirements}</p>
                </div>
              )}

              {/* Meta Info */}
              <div className="border-t pt-4 flex justify-between items-center text-sm text-slate-500">
                <div>Status: {getStatusBadge(selectedEnquiry.status)}</div>
                <div>Submitted: {formatDate(selectedEnquiry.created_at)}</div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WeddingEnquiries;
